import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { API_URL } from "../../../utils/env";

/**
 * OHLCV built from this platform's own indexed fills.
 *
 * `GET /candles` returns bars ascending by time, with money as exact decimal
 * STRINGS (the indexer stores NUMERIC). They are converted to numbers here,
 * at the render boundary, because lightweight-charts takes numbers — that is
 * the only place a float is acceptable in this path.
 *
 * Bars exist only where trades happened. There is no forward-filling anywhere
 * in the stack, so a market that did not trade for a day has a gap, and the
 * chart draws it as one.
 */

export interface RawCandle {
  time: number;
  close_time: number;
  open: string;
  high: string;
  low: string;
  close: string;
  volume: string;
  quote_volume: string;
  trades: number;
  average: string | null;
  taker_buy_volume: string;
}

export interface ChartBar {
  /** Bar open, unix seconds. */
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  trades: number;
}

export const toChartBar = (candle: RawCandle): ChartBar => ({
  time: candle.time,
  open: Number(candle.open),
  high: Number(candle.high),
  low: Number(candle.low),
  close: Number(candle.close),
  volume: Number(candle.volume),
  trades: candle.trades,
});

const CANDLES_URL = `${API_URL}/candles`;

/** How many bars an initial load and each "pan further left" page asks for. */
export const CANDLE_PAGE_SIZE = 500;

/**
 * Fetch bars for one market and resolution.
 *
 * Deliberately NOT abortable. The obvious design — an `AbortController` per
 * request, cancelled in the effect cleanup — was tried and removed, because
 * what protects against a stale response is `requestKeyRef` (below), not the
 * abort: a late response for a market the user has left is discarded either
 * way. The abort added nothing to correctness and cost two things:
 *
 *   - React 18 StrictMode double-invokes the mount effect, so the first request
 *     was aborted on EVERY page load in development. axios rejects from inside
 *     the abort listener, and the OpenTelemetry zone.js patch a wallet SDK
 *     installs logs that rejection from within the listener — before any
 *     `catch` here runs, so it could not be suppressed from this side. Result:
 *     a `CanceledError` in the console on every load, in an app that gates on
 *     console cleanliness.
 *   - Browser devtools and the Chrome extension report an aborted request as
 *     HTTP 503, so every page load appeared to contain a server error that had
 *     never happened.
 *
 * The cost of dropping it is one in-flight GET finishing after the user has
 * moved on. That is a few KB against a phantom error on every load.
 */
export const fetchCandles = async (params: {
  instrumentName: string;
  resolution: string;
  to?: number;
  countback?: number;
}): Promise<ChartBar[]> => {
  const { data } = await axios.get<RawCandle[]>(CANDLES_URL, {
    params: {
      instrument_name: params.instrumentName,
      resolution: params.resolution,
      to: params.to,
      countback: params.countback ?? CANDLE_PAGE_SIZE,
    },
  });
  // Sort ascending. lightweight-charts REQUIRES ascending time and silently
  // renders nothing when given anything else -- an empty chart with a garbage
  // price axis, no error anywhere. The deployed Monad backend returns candles
  // newest-first, which is exactly how that looks in production. The paging
  // merge and the live-bar append below already sort; only this first fetch
  // trusted the server's order, so it was the one path that could break.
  return (Array.isArray(data) ? data : [])
    .map(toChartBar)
    .sort((a, b) => a.time - b.time);
};

export interface UseCandlesResult {
  bars: ChartBar[];
  isLoading: boolean;
  error: Error | null;
  /**
   * Load an older page, prepending it. Resolves to the number of bars the
   * SERVER RETURNED, so a caller can stop paging when history runs out.
   *
   * Deliberately the fetched count rather than a "bars added" count: the merge
   * happens inside a `setBars` updater, which React runs at the next render —
   * after this promise has already resolved — so a figure read from in there
   * would always be 0.
   */
  loadOlder: () => Promise<number>;
  /** Merge one live bar: replaces a bar with the same `time`, else appends. */
  applyLiveBar: (bar: ChartBar) => void;
  /** True once an older page has come back empty — nothing further back exists. */
  reachedStart: boolean;
  /**
   * Identifies the series currently loaded. Changes when the market or the
   * resolution does, which is the moment the whole series is replaced.
   */
  seriesKey: string;
}

/**
 * Historical bars for one market and resolution, with backwards paging.
 *
 * Deliberately not SWR: the series is mutated by both paging (prepend) and the
 * live channel (replace-last), which a cache keyed on the request arguments
 * would fight rather than help.
 */
export const useCandles = (
  instrumentName: string | undefined,
  resolution: string
): UseCandlesResult => {
  const [bars, setBars] = useState<ChartBar[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [reachedStart, setReachedStart] = useState(false);

  // Guards against a slow response for a market/resolution the user has since
  // switched away from overwriting the current series.
  const requestKeyRef = useRef<string>("");
  const loadingOlderRef = useRef(false);
  // Oldest loaded bar time, tracked outside `bars`. `loadOlder` needs it
  // synchronously: read from the `bars` closure, two range events in one frame
  // both compute the same `to` and fire the same request, because React has not
  // re-rendered in between.
  const oldestRef = useRef<number | undefined>(undefined);

  const seriesKey = `${instrumentName ?? ""}:${resolution}`;

  useEffect(() => {
    // Drop the previous series before loading the next. Keeping it would draw
    // the OLD market's (or timeframe's) candles under the new label for as long
    // as the fetch takes, and let the chart auto-fit to them — after which the
    // fit is considered done and the real bars arrive at the wrong zoom.
    setBars([]);
    oldestRef.current = undefined;

    if (!instrumentName) {
      requestKeyRef.current = "";
      return undefined;
    }

    const key = seriesKey;
    requestKeyRef.current = key;

    setIsLoading(true);
    setError(null);
    setReachedStart(false);

    // Every branch re-checks `requestKeyRef`: by the time a response lands the
    // user may be on another market or timeframe, and that response must not
    // touch the series that is now on screen.
    fetchCandles({ instrumentName, resolution })
      .then((loaded) => {
        if (requestKeyRef.current !== key) return;
        setBars(loaded);
        oldestRef.current = loaded[0]?.time;
        setReachedStart(loaded.length < CANDLE_PAGE_SIZE);
      })
      .catch((err) => {
        if (requestKeyRef.current !== key) return;
        setBars([]);
        oldestRef.current = undefined;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (requestKeyRef.current === key) setIsLoading(false);
      });

    return undefined;
  }, [instrumentName, resolution, seriesKey]);

  const loadOlder = useCallback(async (): Promise<number> => {
    if (!instrumentName || loadingOlderRef.current || reachedStart) return 0;

    const key = seriesKey;
    const oldest = oldestRef.current;
    if (oldest === undefined) return 0;

    loadingOlderRef.current = true;
    // Advance the cursor BEFORE awaiting, so a second range event in the same
    // frame cannot request the same window again.
    oldestRef.current = undefined;
    try {
      // `to` is exclusive of the bar we already have: ask for everything up to
      // one second before it.
      const older = await fetchCandles({
        instrumentName,
        resolution,
        to: oldest - 1,
      });
      if (requestKeyRef.current !== key) {
        oldestRef.current = oldest;
        return 0;
      }

      if (older.length === 0) {
        setReachedStart(true);
        oldestRef.current = oldest;
        return 0;
      }

      oldestRef.current = Math.min(oldest, older[0].time);
      setBars((current) => {
        const known = new Set(current.map((b) => b.time));
        const fresh = older.filter((b) => !known.has(b.time));
        if (fresh.length === 0) return current;
        return [...fresh, ...current].sort((a, b) => a.time - b.time);
      });
      if (older.length < CANDLE_PAGE_SIZE) setReachedStart(true);
      return older.length;
    } catch {
      // A failed page is not a failed chart — keep what is drawn, put the
      // cursor back, and let the next pan try again.
      oldestRef.current = oldest;
      return 0;
    } finally {
      loadingOlderRef.current = false;
    }
  }, [instrumentName, resolution, reachedStart, seriesKey]);

  const applyLiveBar = useCallback((bar: ChartBar) => {
    setBars((current) => {
      if (current.length === 0) {
        oldestRef.current = bar.time;
        return [bar];
      }
      const last = current[current.length - 1];

      // The common case by far: an update to the bar still forming.
      if (bar.time === last.time) {
        const next = current.slice();
        next[next.length - 1] = bar;
        return next;
      }
      if (bar.time > last.time) return [...current, bar];

      // Out of order (a reconnect replaying, or a composed bar arriving late).
      const index = current.findIndex((b) => b.time === bar.time);
      if (index === -1) {
        return [...current, bar].sort((a, b) => a.time - b.time);
      }
      const next = current.slice();
      next[index] = bar;
      return next;
    });
  }, []);

  return {
    bars,
    isLoading,
    error,
    loadOlder,
    applyLiveBar,
    reachedStart,
    seriesKey,
  };
};
